import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTracker } from "@ferrivia/auth-web";
import { useFormatters, useMarket, useTranslation } from "@ferrivia/i18n";
import { Button, Text } from "@ferrivia/ui";
import { PVK_ANALYTE_ORDER, type LabAnalyteCode, type LabResult, type ReadLabResultAnalyte } from "@ferrivia/api-contract";
import { useLabResults } from "../../hooks/useLabResults";
import { useLabReportFiles } from "../../hooks/useLabReportFiles";
import { useRefetchOnOverlayClose } from "../../hooks/useRefetchOnOverlayClose";
import { UploadSheet } from "../../components/LabUpload";
import { LabHistoryList } from "./LabHistoryList";
import { LabReportList } from "./LabReportList";
import { AnalyteInfoSheet, type AnalyteInfo } from "../../components/AnalyteInfoSheet";
import { buildTrend, formatAxisDate, formatCollectedOn } from "./trend";
import {
  LabTrendCombinedChart,
  PvkAccordion,
  ResultRow,
  ResultSlip,
  useAnalyteCopy,
  useRangeCopy,
  type PvkAnalyteRow,
} from "@ferrivia/lab-ui";
import s from "./ResultsScreen.module.css";

const PVK_CODES: ReadonlySet<string> = new Set(PVK_ANALYTE_ORDER);

// A Ferrivia client did not choose a laboratory, so none is named on any
// client-facing surface (Ville, 2026-09-05). The clinic console keeps the
// attribution — see RangeAttribution in @ferrivia/lab-ui.
const CLIENT_RANGE_ATTRIBUTION = { attributeLab: false } as const;

export function ResultsScreen() {
  const { t } = useTranslation("client");
  const { tag } = useFormatters();
  const tracker = useTracker();
  const { labelFor } = useAnalyteCopy();
  const { lineFor } = useRangeCopy(CLIENT_RANGE_ATTRIBUTION);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openAnalyte, setOpenAnalyte] = useState<AnalyteInfo | null>(null);
  const { state, refetch } = useLabResults();
  const market = useMarket();
  const { files: reportFiles, refetch: refetchReportFiles } = useLabReportFiles(market === "us");
  const trendSelectionTracked = useRef(false);
  const { pathname } = useLocation();

  // Tab panels stay mounted (app/TabPanels.tsx), so results saved elsewhere —
  // the questionnaire overlay, another tab — would otherwise only show after a
  // reload. Same pattern as HomeScreen.
  const isActive = pathname === "/results";
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      refetch();
      refetchReportFiles();
    }
    wasActiveRef.current = isActive;
  }, [isActive, refetch, refetchReportFiles]);

  useRefetchOnOverlayClose(refetch);
  useRefetchOnOverlayClose(refetchReportFiles);

  function handleSaved() {
    refetch();
    refetchReportFiles();
  }

  function handleOpenOlderUpload() {
    tracker.track("results.add_older_clicked", {});
    setUploadOpen(true);
  }

  function handleOpenUpload() {
    tracker.track("results.upload_sheet_opened", {});
    setUploadOpen(true);
  }

  function handlePvkToggled(expanded: boolean, analyteCount: number) {
    tracker.track("results.pvk_toggled", { expanded, analyte_count: analyteCount });
  }

  // Memoised for the trend below: an inline `[]` on every render would
  // rebuild the whole figure on every render too.
  const results: LabResult[] = useMemo(() => (state.status === "success" ? state.results : []), [state]);
  const latest = results[0] ?? null;
  const trend = useMemo(() => buildTrend(results, labelFor), [results, labelFor]);

  /* Once per visit, not once per column: a drag across the figure passes over
     every draw, and the question this event answers is whether clients use
     the figure at all — not how many pixels they crossed doing it. */
  function handleTrendDrawSelected() {
    if (trendSelectionTracked.current) {
      return;
    }
    trendSelectionTracked.current = true;
    tracker.track("results.trend_draw_selected", { draw_count: trend?.dates.length ?? 0 });
  }

  /* Tapping a reading opens the explainer for that measurement. The sheet is
     handed the analyte's identity and the lab's own metadata — never
     `analyte.value` — so the explanation cannot come to depend on what this
     client measured. */
  function handleAnalyteOpened(analyte: ReadLabResultAnalyte, labCode: string | null) {
    tracker.track("results.analyte_opened", { analyte_code: analyte.analyteCode });
    setOpenAnalyte({
      analyteCode: analyte.analyteCode,
      unit: analyte.unit,
      refText: analyte.refText,
      labCode,
    });
  }

  const mainAnalytes = latest ? latest.analytes.filter((a) => !PVK_CODES.has(a.analyteCode)) : [];
  const pvkAnalytes: ReadLabResultAnalyte[] = latest
    ? PVK_ANALYTE_ORDER.map((code: LabAnalyteCode) => latest.analytes.find((a) => a.analyteCode === code)).filter(
        (a): a is ReadLabResultAnalyte => a !== undefined,
      )
    : [];
  const pvkRows: PvkAnalyteRow[] = pvkAnalytes.map((a) => ({
    analyteCode: a.analyteCode,
    label: labelFor(a.analyteCode),
    value: a.value,
    unit: a.unit,
    rangeText: lineFor(a.refText, latest?.labCode ?? null, a.unit),
  }));

  return (
    <>
      {/* The same header row the home screen sets, down to its four paddings
          and the expression that centres the trailing control on the title's
          first line — see HomeScreen.module.css. What differs between the two
          tabs is only what sits in that trailing slot: home puts the profile
          trigger there, this tab puts the one action the screen has. */}
      <div className={s.header}>
        <Text as="h1" size="xl" weight="bold" className={s.title}>
          {t("results.title", "Tulokset")}
        </Text>
        <button
          type="button"
          className={s.action}
          onClick={handleOpenUpload}
          aria-label={t("results.uploadAction", "Lisää omat tulokset")}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>

      <div className={s.body}>
        {latest ? (
          <>
            {/* The draw's date and its provenance are the slip's head rather
                than a line above the readings: the sentence they used to be
                said the same thing while floating free of what it described,
                and cost ~65px doing it. `labCode` is null exactly when the
                client entered the result themselves — the one thing the lab
                attribution carried that still has to be said on a client
                surface. */}
            <ResultSlip
              when={t("results.collectedOn", "{{date}}", {
                date: formatCollectedOn(latest.collectedOn, tag),
              })}
              provenance={
                latest.labCode === null ? (
                  <span className={s.selfMark}>{t("results.selfReported", "Itse ilmoitettu")}</span>
                ) : undefined
              }
            >
              {mainAnalytes.map((analyte) => (
                <ResultRow
                  key={analyte.analyteCode}
                  label={labelFor(analyte.analyteCode)}
                  value={analyte.value}
                  unit={analyte.unit}
                  rangeText={lineFor(analyte.refText, latest.labCode, analyte.unit)}
                  onClick={() => handleAnalyteOpened(analyte, latest.labCode)}
                />
              ))}

              <PvkAccordion
                rows={pvkRows}
                onRowOpened={(code) => {
                  const analyte = pvkAnalytes.find((a) => a.analyteCode === code);
                  if (analyte) {
                    handleAnalyteOpened(analyte, latest.labCode);
                  }
                }}
                onToggled={(expanded) => handlePvkToggled(expanded, pvkRows.length)}
              />
            </ResultSlip>

            {/* Below the latest draw's own readings, and only once there are
                two draws to draw a line between — see buildTrend.

                Untitled, and deliberately so (Ville, 2026-09-06): the figure
                is not a titled section of its own but a continuation of the
                readings above it. Its own accessible naming carries what a
                heading would have said — the SVG's title lists the analytes
                and the draw count, and the frame is labelled for the reader
                moving between draws. */}
            {trend && (
              <div className={s.trend}>
                <LabTrendCombinedChart
                  series={trend.series}
                  axis={trend.axis}
                  dates={trend.dates}
                  formatAxisDate={(collectedOn) => formatAxisDate(collectedOn, tag)}
                  formatDrawDate={(collectedOn) => formatCollectedOn(collectedOn, tag)}
                  onDrawSelected={handleTrendDrawSelected}
                />
              </div>
            )}

            <LabHistoryList results={results} onAddOlder={handleOpenOlderUpload} />
          </>
        ) : (
          /* The one place a labelled button survives. Everywhere else the
             header's "+" is the way in, but on a screen with nothing else on
             it a 48px glyph in the corner is not a discoverable offer, and
             there is no crowding here for it to solve. */
          <div className={s.emptyBox}>
            <Text as="p" size="md">
              {t("results.empty", "Ei vielä tuloksia — käy tarkistamassa näytteenoton jälkeen.")}
            </Text>
            <Button variant="secondary" onClick={handleOpenUpload}>
              {t("results.uploadCta", "+ Lisää omat tulokset")}
            </Button>
          </div>
        )}
        {market === "us" && <LabReportList files={reportFiles} />}
      </div>

      <UploadSheet
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSaved={handleSaved}
      />
      <AnalyteInfoSheet analyte={openAnalyte} onClose={() => setOpenAnalyte(null)} />
    </>
  );
}
